import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { NextStepProvider, NextStepReact, useNextStep } from 'nextstepjs'
import { useReactRouterAdapter } from 'nextstepjs/adapters/react-router'
import { useFloorPlanPanelStore } from '@/shared/store/useFloorPlanPanelStore'
import { useWebsiteSettingsStore } from '@/shared/store/useWebsiteSettingsStore'
import {
  autoStartTourForPath,
  buildTours,
  FLOOR_PLAN_TOUR,
  tourForPath,
  TOUR_HOME_ROUTE,
  WEBSITE_TOUR,
  WELCOME_TOUR
} from './tours'
import { hasSeenPage, markPageSeen } from './progress'
import { useOnboardingTour } from './useOnboardingTour'
import OnboardingCard from './OnboardingCard'

/** Let the page paint before the spotlight measures its target. */
const AUTO_START_DELAY = 400

/** One tick for react-router to commit a navigation before we look up a selector. */
const ROUTE_SETTLE_DELAY = 150

type Props = {
  children: ReactNode
}

/**
 * Some tours point at a panel the header can close. The page opens it on entry,
 * but a replay can be asked for after the user has closed it, and a closed
 * panel is either gone from the DOM or a zero-size box, so those steps would
 * spotlight nothing.
 */
function prepareTour(tour: string) {
  if (tour === FLOOR_PLAN_TOUR) {
    useFloorPlanPanelStore.getState().setIsOpen(true)
  }
  if (tour === WEBSITE_TOUR) {
    useWebsiteSettingsStore.getState().setIsOpen(true)
  }
}

function TourBridge() {
  const { startNextStep, isNextStepVisible } = useNextStep()
  const navigate = useNavigate()
  const location = useLocation()
  const startRequest = useOnboardingTour(s => s.startRequest)
  const handledRequest = useRef(startRequest)

  const beginTour = useCallback(
    (tour: string) => {
      prepareTour(tour)
      startNextStep(tour)
    },
    [startNextStep]
  )

  // Auto-start: the tour that owns this section, once, on the first visit to it.
  useEffect(() => {
    if (isNextStepVisible) return undefined
    const tour = autoStartTourForPath(location.pathname)
    if (!tour || hasSeenPage(tour)) return undefined
    const id = window.setTimeout(() => beginTour(tour), AUTO_START_DELAY)
    return () => window.clearTimeout(id)
  }, [location.pathname, isNextStepVisible, beginTour])

  // The sidebar's replay button: run the guide for the page the user is on,
  // which is more use than restarting from the welcome screen every time.
  useEffect(() => {
    if (startRequest === handledRequest.current) return undefined
    handledRequest.current = startRequest

    // Replaying ignores the stored flags: the user asked for this guide, so it
    // runs whether or not the page is already marked as seen.
    const owned = tourForPath(location.pathname)
    if (owned) {
      beginTour(owned)
      return undefined
    }

    // Safety net for a page added later without a tour of its own: rather than
    // do nothing, fall back to the welcome tour on the dashboard. Every route
    // that exists today is covered, so this branch should stay unreached.
    navigate(TOUR_HOME_ROUTE)
    const id = window.setTimeout(
      () => beginTour(WELCOME_TOUR),
      ROUTE_SETTLE_DELAY
    )
    return () => window.clearTimeout(id)
  }, [startRequest, location.pathname, navigate, beginTour])

  return null
}

function OnboardingProvider({ children }: Props) {
  const { t, i18n } = useTranslation()

  const steps = useMemo(
    () => buildTours(t),
    // rebuild when language changes so tour copy updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  )

  // The library re-runs its onStart effect whenever this identity changes, so
  // keep all three stable.
  const handleStart = useCallback((tour: string | null) => {
    if (tour) markPageSeen(tour)
  }, [])

  const handleComplete = useCallback((tour: string | null) => {
    if (tour) markPageSeen(tour)
  }, [])

  // Skipping marks this page only. Every other page still offers its own guide
  // on the first visit, which is the whole point of a flag per page.
  const handleSkip = useCallback((_step: number, tour: string | null) => {
    if (tour) markPageSeen(tour)
  }, [])

  return (
    <NextStepProvider>
      <NextStepReact
        steps={steps}
        navigationAdapter={useReactRouterAdapter}
        cardComponent={OnboardingCard}
        cardTransition={{ ease: 'easeOut', duration: 0.25 }}
        onStart={handleStart}
        onComplete={handleComplete}
        onSkip={handleSkip}
        shadowRgb="0, 0, 0"
        shadowOpacity="0.6"
        scrollToTop={false}
        disableConsoleLogs
      >
        <TourBridge />
        {children}
      </NextStepReact>
    </NextStepProvider>
  )
}

export default OnboardingProvider
